import { Inject, Injectable, NgZone } from '@angular/core'
import { has } from 'lodash'
import { MapLayerSettingsStorageService } from '../../../../../core/storage/map-layer-settings-storage.service'
import { GeneralMapLayerService } from './general-map-layer.service'
import { GeneralMapActions } from '../state/action-types'
import { Store } from '@ngrx/store'
import {
    selectFixedLayersVisibilityFromMapZoom,
    selectGeneralMapLayersConfig,
    selectGeneralMapMeasuredCoordinates,
    selectGeneralMapSaveLayersConfig,
    selectGeneralMapShowedInfoBox,
    selectGeneralMapToggleMeasure,
} from '../state/general-map.selectors'
import { filter, take } from 'rxjs/operators'
import { OpenLayersMapService } from '../../../../../shared/open-layers/open-layers-map.service'
import { ISeedElement } from '../../../../../features/map/services/map-color-generator.service'
import { LpisUserDetailDto } from '../../../../../api/backend'
import { MapLpisLayerService } from '../../../../../features/map/services/map-lpis-layer.service'
import { SelectedDepartmentService } from '../../../../../core/services/selected-department.service'
import { selectLpisDatasourceFeature } from '../../../../../features/map/lpis-datasource/state/lpis-datasource.selectors'
import cloneDeep from 'lodash/cloneDeep'
import { MapInfoBoxTypeEnum } from '../../../../../features/map/info-box/map-info-box-type.enum'
import { IMapStateMeasureDrawing } from '../../../../../features/map/interfaces/map-state.interface'
import { Map } from 'ol'
import BaseEvent from 'ol/events/Event'
import { LineString, Polygon } from 'ol/geom'
import { OpenLayersMapMeasureService } from '../../../../../shared/open-layers/open-layers-map-measure.service'
import { GeneralMapZoomService } from './general-map-zoom.service'
import { OpenLayersLayerService } from '../../../../../shared/open-layers/open-layers-layer.service'
import { LpisUserSubjectSearchActions } from '../../../../../features/map/lpis-user-subject-search/state/action-types'
import { selectMapColorGeneratorColorMapByKey } from '../../../../../features/map/common/map-color-generator/state/map-color-generator.selectors'
import { MapColorGeneratorActions } from '../../../../../features/map/common/map-color-generator/state/action-types'
import { GeneralMapService } from './general-map.service'

@Injectable({
    providedIn: 'root',
})
export class GeneralMapHelper {

    // remembers parcel/parcels_list/lpis infobox active before a measurement opened, so the user can
    // dismiss the highlight via the regular close button after measurement ends (per-mapId)
    private previousInfoBoxByMapId: Record<string, MapInfoBoxTypeEnum> = {}

    constructor(@Inject(Store) protected store: Store,
                @Inject(GeneralMapService) protected generalMapService: GeneralMapService,
                @Inject(MapLayerSettingsStorageService) protected mapLayerSettingsStorageService: MapLayerSettingsStorageService,
                @Inject(GeneralMapLayerService) protected generalMapLayerService: GeneralMapLayerService,
                @Inject(OpenLayersMapService) protected openLayersMapService: OpenLayersMapService,
                @Inject(MapLpisLayerService) protected mapLpisLayerService: MapLpisLayerService,
                @Inject(SelectedDepartmentService) protected selectedDepartmentService: SelectedDepartmentService,
                @Inject(OpenLayersMapMeasureService) protected openLayersMapMeasureService: OpenLayersMapMeasureService,
                @Inject(GeneralMapZoomService) protected generalMapZoomService: GeneralMapZoomService,
                @Inject(OpenLayersLayerService) protected openLayersLayerService: OpenLayersLayerService,
                @Inject(NgZone) protected ngZone: NgZone
                ) {
    }

    public beforeSetMap(mapId: string, layersConfig: any, mapOptions: any): void {
        this.ngZone.run(() => {
            this.store.dispatch(GeneralMapActions.initMap({mapId}))
        })
        // this.generalMapService.initMap(mapId)
        if (mapOptions?.saveLayersVisibility) {
            const loadedConfig = this.loadLayers(mapId, mapOptions.storagePrefix)
            if (loadedConfig) {
                this.initCustomLpisLayersInConfig(loadedConfig, layersConfig)
                this.updateScopeConfigVisibility(loadedConfig, layersConfig)
            }
        }

        const layers = this.openLayersLayerService.buildLayersFromConfig(layersConfig)
        const map = new Map({
            target: mapId,
            layers: layers,
            controls: mapOptions.controls,
            interactions: mapOptions.interactions,
            view: mapOptions.view,
            maxTilesLoading: 128,
            keyboardEventTarget: mapOptions.keyboardEventTarget,
        })
        map.set('id', mapId)

        // this.store.dispatch(GeneralMapActions.setMap({ mapId: mapId, map: map, layersConfig, mapOptions }))
        this.generalMapService.setMap(mapId, map)
        this.ngZone.run(() => {
            this.store.dispatch(GeneralMapActions.setLayersConfig({ mapId: mapId, config: layersConfig }))
            this.store.dispatch(GeneralMapActions.setPadding({ mapId: mapId, padding: mapOptions.padding || 70 }))
        })
        this.afterSetMap(mapId, layersConfig, mapOptions)
    }

    public afterSetMap(mapId: string, layersConfig: any, mapOptions: any): void {
        // ==> lpisConfig
        const lpisConfig = this.getSelectorConfigById(layersConfig, 'farmer-blocks-foreign')
        if (lpisConfig && lpisConfig.visible) {
            this.ngZone.run(() => {
                this.store.dispatch(GeneralMapActions.lpisUserSelection({
                    mapId,
                    visible: lpisConfig.isSet,
                    lpisConfig,
                    doNotUpdateMap: true,
                }))
            })
        } else if (lpisConfig) {
            this.ngZone.run(() => {
                this.store.dispatch(GeneralMapActions.lpisUserSelection({ mapId, visible: null, lpisConfig, doNotUpdateMap: true }))
            })
        }
        // ==> parcelEndContractsConfig
        const parcelEndContractsConfig = this.getSelectorConfigById(layersConfig, 'parcely-na-koncicich-smlouvach')
        if (parcelEndContractsConfig && parcelEndContractsConfig.visible) {
            const isSet = this.mapLayerSettingsStorageService.getSavedSettings('parcely-na-koncicich-smlouvach', 'isSet')

            if (isSet) {
                const expDate = this.mapLayerSettingsStorageService.getSavedSettings('parcely-na-koncicich-smlouvach', 'expDate')
                this.ngZone.run(() => {
                    this.store.dispatch(GeneralMapActions.setParcelContractEndingDate({ mapId, expDate: expDate }))
                })
            }
            this.ngZone.run(() => {
                this.store.dispatch(GeneralMapActions.setParcelContractEndingDateVisible({ mapId, visible: true, isSet }))
            })
        }

        // After migration, this was enabled too early and layers setup was overwritten. Moved to general-map.component after move end
        // if (mapOptions.saveLayersVisibility) {
        //     this.store.dispatch(GeneralMapActions.setMapSimpleSaveLayersVisibility({
        //         mapId,
        //         value: mapOptions.saveLayersVisibility,
        //     }))
        // }
        if (mapOptions.saveLayersVisibility) {
            this.ngZone.run(() => {
                this.store.dispatch(GeneralMapActions.setMapSimpleSaveLayersVisibilityAfterInit({
                    mapId,
                    value: mapOptions.saveLayersVisibility,
                }))
            })
        }

        if (mapOptions.storagePrefix) {
            this.ngZone.run(() => {
                this.store.dispatch(GeneralMapActions.setMapSimpleStoragePrefix({ mapId, prefix: mapOptions.storagePrefix }))
            })
        }
    }

    /**
     * Return config object by given id.
     * @param {Object} mainConfig
     * @param {String} id
     * @returns {Object}
     */
    public getSelectorConfigById(mainConfig: any, id: string): any {
        let config = null

        if (mainConfig.id === id) {
            config = mainConfig
        } else {
            mainConfig.layers.forEach((group) => {
                if (group.id === id) {
                    config = group
                } else if (has(group, 'layers') && Array.isArray(group.layers)) {
                    group.layers.forEach((layer) => {
                        if (layer.id === id) {
                            config = layer
                        }
                    })
                }
            })
        }

        return config
    }

    public updateLpisLayerTitle(newConfig: any, lpisConfigLayersLen: number): void {
        let title = lpisConfigLayersLen > 0
            ? `Ostatní uživatelé (${lpisConfigLayersLen})`
            : 'Ostatní uživatelé'

        this.mapLayerSettingsStorageService.setSavedSettings('farmer-blocks-foreign', 'title', title)
        const layerConfig = this.getSelectorConfigById(newConfig, 'farmer-blocks-foreign')

        if (layerConfig) {
            layerConfig.title = title
        }
    }

    public addLayerToMap(mapId: string, layerConfig: any): Promise<void> {
        return this.generalMapLayerService.addLayerToMap(layerConfig, mapId).then(() => {
            this.ngZone.run(() => {
                this.store.dispatch(GeneralMapActions.updateLayerConfigPropertyByLayerId({mapId, layerId: layerConfig.id, property: 'visible', value: true}))
                this.removeDependentLayers(mapId, layerConfig)
            })
        })
    }

    public removeDependentLayers(mapId: string, layerConfig): void {
        let layersToRemove = []

        if (layerConfig.comboBox && typeof layerConfig.comboBox === 'object') {
            for (const key in layerConfig.comboBox) {
                if (key !== layerConfig.id) {
                    layersToRemove.push(key)
                }
            }
        }

        if (layerConfig.hideIfVisible) {
            layersToRemove = Array.from(new Set(layersToRemove.concat(layerConfig.hideIfVisible)))
        }

        this.store.select(selectGeneralMapLayersConfig(mapId)).pipe(take(1)).subscribe(layersConfig => {
            layersToRemove.forEach(layerToRemove => {
                const config = this.getSelectorConfigById(layersConfig, layerToRemove)

                if (config?.selector === 'group') {
                    this.removeGroup(mapId, config)
                } else {
                    this.removeLayerByName(mapId, layerToRemove)
                }
            })
        })
    }

    public removeGroup(mapId: string, groupConfig: any): void {
        if (groupConfig.selector !== 'group' || !Array.isArray(groupConfig.layers)) {
            return
        }

        this.store.dispatch(GeneralMapActions.updateLayerConfigPropertyByLayerId({mapId, layerId: groupConfig.id, property: 'visible', value: false}))

        groupConfig.layers.map((layer) => layer.id).forEach((layerToRemove) => this.removeLayerByName(mapId, layerToRemove))
    }

    public removeLayerByName(mapId: string, layerName: string): Promise<void> {
        return new Promise(resolve => {
            this.generalMapService.map$.pipe(
                filter(map => map[mapId] !== null && map[mapId] !== undefined), // Check if map is not null or undefined
                take(1), // Execute the code only once
            ).subscribe(map => {
                this.openLayersMapService.removeLayerByName(layerName, map[mapId])
                this.ngZone.run(() => {
                    this.store.dispatch(GeneralMapActions.updateLayerConfigPropertyByLayerId({mapId, layerId: layerName, property: 'visible', value: false}))
                    resolve()
                })
            })
        })
    }

    public saveLayersConfig(mapId: string): void {
        this.store.select(selectGeneralMapSaveLayersConfig(mapId)).pipe(take(1)).subscribe(state => {
            if (state.saveLayersVisibility) {
                const storagePrefix = state.storagePrefix
                this.saveLayers(mapId, state.layersConfig, storagePrefix)
            }
        })

    }

    public addLpisLayer(mapId: string, layerColor: ISeedElement, lpisId: number, lpisUserInfo: LpisUserDetailDto): any {
        this.store.select(selectGeneralMapLayersConfig(mapId)).pipe(take(1)).subscribe(layersConfig => {
            let updatedLayersConfig = cloneDeep(layersConfig)
            let lpisLayersConfigGroup = this.mapLpisLayerService.getLpisUserConfigGroup(updatedLayersConfig)

            const layerConfig = this.mapLpisLayerService.getLpisLayerConfig(lpisId, layerColor, lpisUserInfo)
            layerConfig.visible = true
            lpisLayersConfigGroup.layers.push(layerConfig)

            this.addLayerToMap(mapId, layerConfig)
            this.updateLpisLayerTitle(updatedLayersConfig, lpisLayersConfigGroup.layers.length)

            this.store.dispatch(GeneralMapActions.setLayersConfig({mapId, config: updatedLayersConfig}))

            if (lpisLayersConfigGroup.layers?.length === 1) {
                this.switchDefaultLpisLayer(mapId,  updatedLayersConfig, true)
            }
        })
    }

    public setLayerTitle(mapId: string, layerId: string, newTitle: string): void {
        if (newTitle) {
            this.mapLayerSettingsStorageService.setSavedSettings(layerId, 'title', newTitle)
        }

        this.store.select(selectGeneralMapLayersConfig(mapId)).pipe(take(1)).subscribe(layersConfig => {
            const updateLayersConfig = cloneDeep(layersConfig)
            this.store.dispatch(GeneralMapActions.setLayersConfig({mapId, config: this.updateLayersFromSettings(updateLayersConfig, this.mapLayerSettingsStorageService.getAllSavedSettings())}))
        })
    }

    public toggleMeasure(mapId: string, type: MapInfoBoxTypeEnum): void {
        this.store.select(selectGeneralMapToggleMeasure(mapId)).pipe(take(1)).subscribe(({ currentType, currentFeatureInfo, layersOutsideSelector }) => {
            const onlyClose = type === currentType
            if (currentFeatureInfo) {
                this.store.dispatch(GeneralMapActions.resetMapSimpleCurrentFeatureInfo({ mapId }))
            }
            const isSelectionType =
                currentType === MapInfoBoxTypeEnum.lpis ||
                currentType === MapInfoBoxTypeEnum.parcel ||
                currentType === MapInfoBoxTypeEnum.parcels_list
            const isCurrentMeasure =
                currentType === MapInfoBoxTypeEnum.measure_area ||
                currentType === MapInfoBoxTypeEnum.measure_distance
            const isOpeningMeasure =
                type === MapInfoBoxTypeEnum.measure_distance ||
                type === MapInfoBoxTypeEnum.measure_area

            // keep parcel/parcels_list/lpis selection visible when switching to a measuring tool
            const keepSelection = isSelectionType && isOpeningMeasure

            // remember underlying selection so we can restore the infobox after measurement ends
            if (keepSelection) {
                this.previousInfoBoxByMapId[mapId] = currentType
            } else if (isOpeningMeasure && !isCurrentMeasure) {
                // opening measurement from a non-selection state — drop any stale memo
                delete this.previousInfoBoxByMapId[mapId]
            }

            // if there is some info box
            if (currentType != null) {
                // and it is measuring one
                if (isCurrentMeasure) {
                    // stop measuring
                    this.disableDrawing(mapId)
                    this.generalMapService.measurementClosedAt = Date.now()
                } else {
                    // otherwise remove all vector layers outside selector
                    this.generalMapService.map$.pipe(
                        filter(map => map[mapId] !== null && map[mapId] !== undefined), // Check if map is not null or undefined
                        take(1), // Execute the code only once
                    ).subscribe(map => {
                        layersOutsideSelector.forEach((layer: any) => {
                            this.openLayersMapService.removeLayerByName(layer.id, map[mapId])
                            this.store.dispatch(GeneralMapActions.updateLayerConfigPropertyByLayerId({mapId, layerId: layer.id, property: 'visible', value: false}))
                        })
                    })
                }

                // hide existing info box (skip when keeping selection visible during measurement)
                if (!keepSelection) {
                    this.store.dispatch(GeneralMapActions.closeInfobox({mapId, infoBoxType: currentType}))
                }
            }
            // we are not closing existing info box
            if (!onlyClose) {
                this.store.dispatch(GeneralMapActions.resetMapMeasuredLength({ mapId }))
                this.store.dispatch(GeneralMapActions.resetMapMeasuredArea({ mapId }))
                this.store.dispatch(GeneralMapActions.resetMapSimpleMapMeasuredCoordinates({ mapId }))

                if (type === MapInfoBoxTypeEnum.measure_distance || type === MapInfoBoxTypeEnum.measure_area) {
                    this.store.dispatch(GeneralMapActions.openInfoBox({ mapId, infoBoxType: type }))

                    this.generalMapService.map$.pipe(
                        filter(map => map[mapId] !== null && map[mapId] !== undefined), // Check if map is not null or undefined
                        take(1), // Execute the code only once
                    ).subscribe(map => {
                        // Defer Draw interaction creation to let the DOM update after
                        // the infobox opens and hasOpenedPanel class is applied. Without
                        // this, OL's internal viewport size can be stale in the AOT build,
                        // causing Polygon Draw clicks to not register on some pages.
                        setTimeout(() => {
                            map[mapId].updateSize()
                            this.ngZone.run(() => {
                                this.createDrawing(mapId, map[mapId], type)
                            })
                        })
                    })
                }
            } else if (isCurrentMeasure) {
                // closing measurement — bring back the parcel/lpis infobox so the user can dismiss the highlight
                this.tryRestorePreviousInfoBoxAfterMeasure(mapId)
            }
        })

    }

    public tryRestorePreviousInfoBoxAfterMeasure(mapId: string): void {
        const previous = this.previousInfoBoxByMapId[mapId]
        if (previous == null) {
            return
        }
        delete this.previousInfoBoxByMapId[mapId]
        this.store.dispatch(GeneralMapActions.openInfoBox({ mapId, infoBoxType: previous }))
    }

    public disableDrawing(mapId: string): void  {
        this.store.dispatch(GeneralMapActions.resetMapMeasuredLength({ mapId }))
        this.store.dispatch(GeneralMapActions.resetMapMeasuredArea({ mapId }))
        this.store.dispatch(GeneralMapActions.resetMapSimpleMapMeasuredCoordinates({ mapId }))

        // do not clone OL map objects ever ;-)
        const drawing: IMapStateMeasureDrawing = this.generalMapService.getMaMeasureDrawing(mapId)

        if (drawing) {
            this.generalMapService.map$.pipe(
                filter(map => map[mapId] !== null && map[mapId] !== undefined), // Check if map is not null or undefined
                take(1), // Execute the code only once
            ).subscribe(map => {
                if (drawing.draw) {
                    map[mapId].removeInteraction(drawing.draw)
                }
                if (drawing.modify) {
                    map[mapId].removeInteraction(drawing.modify)
                }
                if (drawing.vectorLayer) {
                    map[mapId].removeLayer(drawing.vectorLayer)
                }

                this.generalMapService.resetMeasureDrawing(mapId)
            })
        }
    }

    public enableDrawing(mapId: string): void {
        this.store.select(selectGeneralMapShowedInfoBox(mapId)).pipe(take(1)).subscribe((currentType: MapInfoBoxTypeEnum) => {
            this.generalMapService.map$.pipe(
                filter(map => map[mapId] !== null && map[mapId] !== undefined), // Check if map is not null or undefined
                take(1), // Execute the code only once
            ).subscribe(map => {
                this.createDrawing(mapId, map[mapId], currentType)
            })
        })
    }

    public toggleAddParcelToGroup(mapId: string): void {
        this.store.select(selectGeneralMapShowedInfoBox(mapId)).pipe(take(1)).subscribe((showedInfoBox: MapInfoBoxTypeEnum) => {
            this.clearLingeringSelection(mapId)
            if(showedInfoBox != null) {
                this.store.dispatch(GeneralMapActions.closeInfobox({mapId, infoBoxType: showedInfoBox}))
            }
            this.store.dispatch(GeneralMapActions.openInfoBox({ mapId, infoBoxType: MapInfoBoxTypeEnum.parcel_to_group }))
        })

    }

    public toggleParcelOrLpisvSearch(mapId: string): void {
        this.store.select(selectGeneralMapShowedInfoBox(mapId)).pipe(take(1)).subscribe((showedInfoBox: MapInfoBoxTypeEnum) => {
            this.clearLingeringSelection(mapId)
            if(showedInfoBox != null) {
                this.store.dispatch(GeneralMapActions.closeInfobox({mapId, infoBoxType: showedInfoBox}))
            }
            this.store.dispatch(GeneralMapActions.openInfoBox({ mapId, infoBoxType: MapInfoBoxTypeEnum.parcel_or_lpisv_search }))
        })
    }

    public openOlInfoBox(type: MapInfoBoxTypeEnum, mapId: string, features: Array<any>, backlink: boolean = false, zoom: boolean = false): void {
        this.ngZone.run(() => {
            this.store.dispatch(GeneralMapActions.createMapSimpleFeatureGroupLayerIfNotExists({
                mapId,
                layerName: 'highlight-overlay',
                zIndex: 980
            }))
        })

        this.generalMapLayerService.removeAllFeaturesFromLayerByName('highlight-overlay', mapId).then( () => {
            features.forEach(feature => feature.properties.mapColorStyle = 'yellow-semi')
            this.generalMapLayerService.setGeoJsonsToLayerByName(features, 'highlight-overlay', mapId).then(() => {
                this.generalMapLayerService
                    .getLayerFeatures(mapId, 'highlight-overlay')
                    .then(features => {
                        switch (type) {
                            case MapInfoBoxTypeEnum.lpis:
                                // this.store.dispatch(GeneralMapActions.setupLpisDetail({ mapId, feature: features[0] })) // Detected unserializable action at "feature"
                                this.ngZone.run(() => {
                                    this.generalMapService.setDetailFeature(mapId, features[0])
                                    this.store.dispatch(GeneralMapActions.openInfoBox({ mapId, infoBoxType: type }))
                                })
                                if (zoom) {
                                this.generalMapZoomService.zoomMapToFeature(mapId, features[0], 144, 200)
                                }
                                break
                            case MapInfoBoxTypeEnum.parcel:
                                // this.store.dispatch(GeneralMapActions.setupParcelDetail({ mapId, feature: features[0], backlink }))
                                this.ngZone.run(() => {
                                    this.generalMapService.setDetailFeature(mapId, features[0], backlink)
                                    this.store.dispatch(GeneralMapActions.setBacklink({ mapId, backlink }))
                                    this.store.dispatch(GeneralMapActions.openInfoBox({ mapId, infoBoxType: type }))
                                })
                                if (zoom) {
                                    this.generalMapZoomService.zoomMapToFeature(mapId, features[0], 700, 200)
                                }
                                break
                            case MapInfoBoxTypeEnum.parcels_list:
                                this.ngZone.run(() => {
                                    this.store.dispatch(GeneralMapActions.setMapSimpleFeatures({ mapId, features: cloneDeep(features) }))
                                    this.store.dispatch(GeneralMapActions.setupParcelList({ mapId }))
                                    this.store.dispatch(GeneralMapActions.openInfoBox({ mapId, infoBoxType: type }))
                                })
                                // parcel list is always zoomed
                                this.generalMapZoomService.zoomMapToAllFeaturesOfLayer(mapId, 'highlight-overlay')
                                break
                            case MapInfoBoxTypeEnum.building:
                                this.ngZone.run(() => {
                                    this.generalMapService.setDetailFeature(mapId, features[0], backlink)
                                    this.store.dispatch(GeneralMapActions.setBacklink({ mapId, backlink }))
                                    this.store.dispatch(GeneralMapActions.openInfoBox({ mapId, infoBoxType: type }))
                                })
                                if (zoom) {
                                    this.generalMapZoomService.zoomMapToFeature(mapId, features[0], 700, 200)
                                }
                                break
                        }
                    })
            })
        })
    }

    public saveLayers(mapId: string, layersConfig: any, storagePrefix: string): void {
        localStorage.setItem(
            (storagePrefix ? storagePrefix + '.' : '') + 'layerSelector.' + mapId,
            JSON.stringify(this.getLayersToSave(layersConfig)))
    }

    public switchLayer(layerConfig, mapId: string): Promise<void> {
        return layerConfig.visible ? this.removeLayerByName(mapId, layerConfig.id) : this.addLayerToMap(mapId, layerConfig)
    }

    public removeLpisLayer(mapId: string, removeLayer: any): void {
        this.generalMapService.map$.pipe(
            filter(map => map[mapId] !== null && map[mapId] !== undefined), // Check if map is not null or undefined
            take(1), // Execute the code only once
        ).subscribe(map => {
            this.openLayersMapService.removeLayerByName(removeLayer.id, map[mapId])
            this.store.dispatch(GeneralMapActions.updateLayerConfigPropertyByLayerId({mapId, layerId: removeLayer.id, property: 'visible', value: false}))
        })

        // remove layer from global map config
        this.store.select(selectGeneralMapLayersConfig(mapId)).pipe(take(1)).subscribe(layersConfig => {
            const updatedLayersConfig = cloneDeep(layersConfig)
            const lpisUserConfigGroup = this.mapLpisLayerService.getLpisUserConfigGroup(updatedLayersConfig)

            lpisUserConfigGroup.layers = lpisUserConfigGroup.layers.filter(layer => layer.id !== removeLayer.id)

            this.updateLpisLayerTitle(updatedLayersConfig, lpisUserConfigGroup.layers.length)

            this.store.dispatch(GeneralMapActions.setLayersConfig({mapId, config: updatedLayersConfig }))

            if (lpisUserConfigGroup.layers?.length === 0) {
                this.switchDefaultLpisLayer(mapId, updatedLayersConfig, false)
            }
        })
    }

    public switchComboBox(layerId: string, mapId: string): any {
        this.store.select(selectGeneralMapLayersConfig(mapId)).pipe(take(1)).subscribe(layersConfig => {
            const layerConfig = this.getSelectorConfigById(layersConfig, layerId)

            if (layerConfig?.selector === 'switch') {
                this.addLayerToMap(mapId, layerConfig)
            } else {
                this.store.dispatch(GeneralMapActions.updateLayerConfigPropertyByLayerId({mapId, layerId: layerId, property: 'visible', value: true}))
                this.removeDependentLayers(mapId, layerConfig)
            }
        })
    }

    // ==> Zoom
    public fixedLayersVisibilityFromMapZoom(mapId: string): Promise<void> {
        return new Promise(resolve => {
            this.generalMapService.map$.pipe(
                filter(map => map[mapId] !== null && map[mapId] !== undefined), // Check if map is not null or undefined
                take(1), // Execute the code only once
            ).subscribe(map => {
                this.store.select(selectFixedLayersVisibilityFromMapZoom(mapId)).pipe(take(1)).subscribe(({ layersConfig, parcelToGroupDrawing }) => {
                    const zoom = map[mapId].getView().getZoom()
                    // const toDisable = this.getVisibleLayersFromZoom(layersConfig, zoom)
                    // const toEnable = this.getUnvisibleLayersFromZoom(layersConfig, zoom)
                    const toDisable = this.getUnvisibleLayersFromZoom(layersConfig, zoom)
                    const toEnable = this.getVisibleLayersFromZoom(layersConfig, zoom)

                    if (toDisable.length > 0) {
                        toDisable.forEach(layer => {
                            if (layer.visible) {
                                this.removeLayerByName(mapId, layer.id)
                            }

                            if (layer.zoomVisibilityAffectsSelectorDisabled === true) {
                                this.ngZone.run(() => {
                                    this.store.dispatch(GeneralMapActions.updateLayerConfigPropertyByLayerId({mapId, layerId: layer.id, property: 'selectorDisabled', value: true}))
                                })
                            }
                        })
                    }
                    if (toEnable.length > 0) {
                        toEnable.map(layer => {
                            if ((layer.zoomVisibilityAffectsSelectorDisabled && !layer.selectorDisabled) || parcelToGroupDrawing?.allParcelsLayerVisible) {
                                return
                            } else if (layer.zoomVisibilityAffectsSelectorDisabled && layer.selectorDisabled) {
                                this.ngZone.run(() => {
                                    this.store.dispatch(GeneralMapActions.updateLayerConfigPropertyByLayerId({mapId, layerId: layer.id, property: 'selectorDisabled', value: false}))
                                })
                            }

                            this.addLayerToMap(mapId, layer)
                        })
                    }
                    resolve()
                })
            })
        })
    }
    // <==

    public resetMap(mapId: string): void {
        this.generalMapService.resetMap(mapId)
        this.generalMapService.resetFeatureGroupLayers(mapId)
    }

    public switchDefaultLpisLayer(mapId: string, newConfig: any, hide: boolean): void {
        const lpisForeignDefaultLayerConfig = this.getSelectorConfigById(newConfig, 'farmer-blocks-foreign')

        if (!lpisForeignDefaultLayerConfig.visible) {
            return
        }

        this.store.select(selectLpisDatasourceFeature).pipe(take(1)).subscribe( state => {
            let newFilter = state.filterArgs

            if (hide) {
                newFilter = {
                    type: 'And',
                    args: [
                        {
                            type: 'PropertyIsEqualTo',
                            propertyName: 'id_uz',
                            literal: 0
                        }
                    ].concat(state.filterArgs)
                }
            }

            if (this.selectedDepartmentService.isDepartmentSelected()) {
                newFilter = {
                    type: 'And',
                    args: [
                        {
                            type: 'PropertyIsLike',
                            propertyName: 'department_id',
                            literal: '*|' + this.selectedDepartmentService.getSelectedDepartmentId() + '|*'
                        },
                        newFilter
                    ]
                }
            }

            this.generalMapService.map$.pipe(
                filter(map => map[mapId] !== null && map[mapId] !== undefined), // Check if map is not null or undefined
                take(1), // Execute the code only once
            ).subscribe(map => {
                this.openLayersMapService.removeLayerByName('farmer-blocks-foreign', map[mapId])
                this.ngZone.run(() => {
                    this.store.dispatch(GeneralMapActions.updateLayerConfigPropertyByLayerId({
                        mapId,
                        layerId: 'farmer-blocks-foreign',
                        property: 'visible',
                        value: false
                    }))
                })
                this.mapLayerSettingsStorageService.setSavedSettings(lpisForeignDefaultLayerConfig.id, 'filter', newFilter)
                this.addLayerToMap(mapId, lpisForeignDefaultLayerConfig)
            })

        })
    }

    private createDrawing(mapId: string, map: Map, type: MapInfoBoxTypeEnum): void {
        const measureDrawing = this.openLayersMapMeasureService.createDrawing(
            type,
            map,
            (evt: BaseEvent) => {
                const geom = evt.target

                this.store.select(selectGeneralMapMeasuredCoordinates(mapId)).pipe(take(1)).subscribe(previousCoordinates => {
                    if (geom instanceof Polygon) {
                        const presentCoordinates = geom.getCoordinates()[0].length

                        this.ngZone.run(() => {
                            if (presentCoordinates > 1 && (previousCoordinates != 0 && previousCoordinates != presentCoordinates)) {
                                this.store.dispatch(GeneralMapActions.setMapMeasuredLength({
                                    mapId,
                                    measuredLength: new LineString(geom.getCoordinates()[0]).getLength()
                                }))
                            } else if (previousCoordinates != 0 && previousCoordinates != presentCoordinates) {
                                this.store.dispatch(GeneralMapActions.resetMapMeasuredLength({ mapId }))
                            }

                            if (presentCoordinates < 3) {
                                this.store.dispatch(GeneralMapActions.resetMapMeasuredArea({ mapId }))
                            } else if (previousCoordinates != 0 && previousCoordinates != presentCoordinates) {
                                this.store.dispatch(GeneralMapActions.setMapMeasuredArea({ mapId, measuredArea: geom.getArea() }))
                            }

                            this.store.dispatch(GeneralMapActions.setMapMeasuredCoordinates({
                                mapId,
                                measuredCoordinates: presentCoordinates
                            }))
                        })
                    } else if (geom instanceof LineString) {
                        const presentCoordinates = geom.getCoordinates().length

                        this.ngZone.run(() => {
                            if (presentCoordinates < 2) {
                                this.store.dispatch(GeneralMapActions.resetMapMeasuredLength({mapId}))
                            } else if (previousCoordinates != 0 && previousCoordinates != presentCoordinates) {
                                this.store.dispatch(GeneralMapActions.setMapMeasuredLength({mapId, measuredLength: geom.getLength()}))
                            }

                            this.store.dispatch(GeneralMapActions.setMapMeasuredCoordinates({mapId, measuredCoordinates: presentCoordinates}))
                        })
                    }
                })
            },
            (evt: BaseEvent) => {
                const geom = evt.target

                if (geom instanceof Polygon) {
                    const presentCoordinates = geom.getCoordinates()[0].length

                    this.ngZone.run(() => {
                        if (presentCoordinates > 1) {
                            this.store.dispatch(GeneralMapActions.setMapMeasuredLength({mapId, measuredLength: new LineString(geom.getCoordinates()[0]).getLength()}))
                        } else {
                            this.store.dispatch(GeneralMapActions.resetMapMeasuredLength({mapId}))
                        }

                        if (presentCoordinates < 3) {
                            this.store.dispatch(GeneralMapActions.resetMapMeasuredArea({mapId}))
                        } else {
                            this.store.dispatch(GeneralMapActions.setMapMeasuredArea({mapId, measuredArea: geom.getArea()}))
                        }
                    })
                } else if (geom instanceof LineString) {
                    const presentCoordinates = geom.getCoordinates().length

                    this.ngZone.run(() => {
                        if (presentCoordinates < 2) {
                            this.store.dispatch(GeneralMapActions.resetMapMeasuredLength({mapId}))
                        } else {
                            this.store.dispatch(GeneralMapActions.setMapMeasuredLength({mapId, measuredLength: geom.getLength()}))
                        }
                    })
                }
            })

        // this.store.dispatch(GeneralMapActions.setMapMeasureDrawing({mapId, measureDrawing: measureDrawing}))
        this.generalMapService.setMeasureDrawing(mapId, measureDrawing)
    }

    private getLayersToSave(config: any): Array<any> {
        const result: Array<any> = []

        if (config.id !== undefined && config.visible !== undefined) {
            let item: LayerToSaveConfig = { id: config.id, visible: config.visible }

            if (config.isSet !== undefined) {
                item.isSet = config.isSet
            }

            if (config.expDate !== undefined) {
                item.expDate = config.expDate
            }

            if (config.lpisId !== undefined) {
                item.acreage = config.acreage
                item.title = config.title
                item.userIdent = config.userIdent
            }

            result.push(item)
        }

        if (Array.isArray(config.layers)) {
            config.layers.forEach((layer) => {
                this.getLayersToSave(layer).forEach(value => result.push(value))
            })
        }

        return result
    }

    private updateLayersFromSettings(config: any, settings: any): any {
        if (config?.layers && Array.isArray(config.layers)) {
            config.layers.forEach(value => this.updateLayersFromSettings(value, settings))
        } else if (config.settings) {
            if (settings[config.id] && settings[config.id].title) {
                config.title = settings[config.id].title
            } else if (config.defaultTitle) {
                config.title = config.defaultTitle
            }
        }

        return config
    }

    // ==> Zoom
    private getVisibleLayersFromZoom(config, zoom: number): any {
        const result = []

        this.forEachLayer(config, (layer) => {
            if (layer.visible === false
                && this.isVisibilitySet(layer.maxZoomVisibility, layer.minZoomVisibility)
                && this.isZoomBetweenVisibility(zoom, layer.maxZoomVisibility, layer.minZoomVisibility)
            ) {
                result.push(layer)
            }
        })

        return result
    }

    private isZoomBetweenVisibility(zoom: number, maxVisibility: number, minVisibility: number): boolean {
        if (typeof(maxVisibility) === 'number'
            && typeof(minVisibility) === 'number'
            && minVisibility <= zoom
            && maxVisibility >= zoom
        ) {
            return true
        } else if (typeof(maxVisibility) === 'number'
            && typeof(minVisibility) !== 'number'
            && maxVisibility >= zoom
        ) {
            return true
        } else if (typeof(maxVisibility) !== 'number'
            && typeof(minVisibility) === 'number'
            && minVisibility <= zoom
        ) {
            return true
        } else {
            return false
        }
    }

    private getUnvisibleLayersFromZoom(config, zoom: number): any {
        const result = []

        this.forEachLayer(config, (layer) => {
            if ((layer.visible || layer.zoomVisibilityAffectsSelectorDisabled === true)
                && this.isVisibilitySet(layer.maxZoomVisibility, layer.minZoomVisibility)
                && !this.isZoomBetweenVisibility(zoom, layer.maxZoomVisibility, layer.minZoomVisibility)
            ) {
                result.push(layer)
            }
        })

        return result
    }
    // <==

    private forEachLayer(layerConfig, func: Function): void {
        func(layerConfig)

        if (layerConfig.layers && Array.isArray(layerConfig.layers)) {
            for (let i = 0; i < layerConfig.layers.length; i++) {
                this.forEachLayer(layerConfig.layers[i], func)
            }
        }
    }

    private isVisibilitySet(maxVisibility, minVisibility): boolean {
        if (typeof (maxVisibility) !== 'number' && typeof (minVisibility) !== 'number') {
            return false
        }

        return true
    }

    private loadLayers(mapId: string, storagePrefix: string): any {
        return JSON.parse(localStorage.getItem((storagePrefix ? storagePrefix + '.' : '') + 'layerSelector.' + mapId))
    }

    /**
     * This mutates config params - it updates lpis_user_* layers with data from loadedConfig param.
     * @param loadedConfig
     * @param config
     * @private
     */
    private initCustomLpisLayersInConfig(loadedConfig: any, config: any): void {
        const lpisForeignLayer = loadedConfig.find(config => config.id === 'farmer-blocks-foreign')
        // load custom LPIS-user layers
        this.ngZone.run(() => {
            this.store.dispatch(MapColorGeneratorActions.getNextColors({
                num: loadedConfig.filter(savedConfig => savedConfig.id.startsWith('lpis_user_')).length,
                ident: 'lpis',
            }))
        })
        let colors$ = this.store.select(selectMapColorGeneratorColorMapByKey('lpis'))

        colors$.pipe(take(1)).subscribe(colors => {
            const isSet = lpisForeignLayer.isSet === undefined ? false : lpisForeignLayer.isSet
            const lpisIds = []
            const lpisUserConfigGroup = config?.layers.find(layerGroup => layerGroup.id === 'lpis_user')
            let i = 0
            loadedConfig.forEach(savedConfig => {
                if (savedConfig.id.startsWith('lpis_user_')) {
                    const lpisId = parseInt(savedConfig.id.replace('lpis_user_', ''))
                    const layerConfig = this.mapLpisLayerService.getLpisLayerConfig(lpisId, colors[i++], {})

                    layerConfig.visible = savedConfig.visible && isSet
                    layerConfig.title = savedConfig.title
                    layerConfig.userIdent = savedConfig.userIdent
                    layerConfig.acreage = savedConfig.acreage

                    lpisUserConfigGroup.layers.push(layerConfig)

                    lpisIds.push(lpisId)
                }
            })
            this.ngZone.run(() => {
                this.store.dispatch(LpisUserSubjectSearchActions.updateFilterCriteria({ criteriaFragment: { exceptLpisIds: lpisIds } }))
            })
        })
    }

    /**
     * Updates config visibility attribute from given config.
     * @param {Object} config
     * @param {Object} scopeConfig scope config object which will be returned
     */
    private updateScopeConfigVisibility(config: Array<any>, scopeConfig: any): void {
        if (Array.isArray(config)) {
            let visibleOne = false
            config.forEach((value) => {
                const selConfig = this.getSelectorConfigById(scopeConfig, value.id)
                if (selConfig) {
                    selConfig.visible = value.visible

                    if (value.isSet) {
                        selConfig.isSet = value.isSet
                    }

                    if (value.expDate) {
                        selConfig.expDate = value.expDate
                    }
                }

                if ((value.id === 'parcel_group' || value.id === 'contract_group') && value.visible === true && !selConfig) {
                    visibleOne = true
                }
            })
            // CA-12810 - if parcel_group layers group is empty, than its deleted from scopeConfig, but if
            // it was saved in local storage as visible before, there must be new visible group (the default one)
            // otherwise no groups switch box is visible
            if (visibleOne) {
                let selConfig = this.getSelectorConfigById(scopeConfig, 'parcel_owner')
                if (selConfig) {
                    selConfig.visible = true
                }
            }

        }
    }

    // wipes any leftover parcel/LPIS highlight + state when starting a takeover flow
    // (parcel_to_group, parcel_or_lpisv_search) — without this, a highlight surviving from
    // a previous selection→measure session would have no infobox to dismiss it
    private clearLingeringSelection(mapId: string): void {
        delete this.previousInfoBoxByMapId[mapId]
        this.generalMapLayerService.removeAllFeaturesFromLayerByName('highlight-overlay', mapId)
        this.store.dispatch(GeneralMapActions.setMapSimpleFeatures({ mapId, features: [] }))
        this.store.dispatch(GeneralMapActions.setHeader({ mapId, value: null }))
    }
}

interface LayerToSaveConfig {
    id: any;
    visible: boolean;
    isSet?: boolean;
    expDate?: Date;
    acreage?: number;
    title?: string;
    userIdent?: string;
}
